/**
 * Safe localStorage utility functions
 * 
 * Wraps localStorage access in try/catch to handle:
 * - QuotaExceededError (storage full)
 * - SecurityError (private browsing mode)
 * - Disabled storage
 * 
 * All functions return null on error to prevent crashes
 */

/**
 * Safely get an item from localStorage
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if key doesn't exist or error occurs
 * @returns {string|null} Stored value or defaultValue
 */
export const safeGetItem = (key, defaultValue = null) => {
  if (!key || typeof key !== 'string') {
    return defaultValue;
  }
  
  try {
    const item = localStorage.getItem(key);
    return item !== null ? item : defaultValue;
  } catch (error) {
    // Handle quota exceeded, security errors, or disabled storage
    console.warn(`[storage] Failed to get item "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely set an item in localStorage
 * @param {string} key - Storage key
 * @param {*} value - Value to store (will be stringified if not a string)
 * @returns {boolean} True if successful, false otherwise
 */
export const safeSetItem = (key, value) => {
  if (!key || typeof key !== 'string') {
    return false;
  }
  
  try {
    // Convert value to string if needed
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    // Handle quota exceeded, security errors, or disabled storage
    if (error.name === 'QuotaExceededError') {
      console.warn(`[storage] Storage quota exceeded for key "${key}"`);
    } else {
      console.warn(`[storage] Failed to set item "${key}":`, error);
    }
    return false;
  }
};

/**
 * Safely remove an item from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} True if successful, false otherwise
 */
export const safeRemoveItem = (key) => {
  if (!key || typeof key !== 'string') {
    return false;
  }
  
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[storage] Failed to remove item "${key}":`, error);
    return false;
  }
};

/**
 * Safely get and parse JSON from localStorage
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if key doesn't exist or parse fails
 * @returns {*} Parsed JSON value or defaultValue
 */
export const safeGetJSON = (key, defaultValue = null) => {
  const item = safeGetItem(key, null);
  if (item === null) {
    return defaultValue;
  }
  
  try {
    return JSON.parse(item);
  } catch (error) {
    console.warn(`[storage] Failed to parse JSON for key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely set JSON value in localStorage
 * @param {string} key - Storage key
 * @param {*} value - Value to stringify and store
 * @returns {boolean} True if successful, false otherwise
 */
export const safeSetJSON = (key, value) => {
  try {
    const stringValue = JSON.stringify(value);
    return safeSetItem(key, stringValue);
  } catch (error) {
    console.warn(`[storage] Failed to stringify value for key "${key}":`, error);
    return false;
  }
};

